import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { ExternalLink, Heart, Play, Share2 } from "lucide-react";
import Typography from "./ui/Typography";
import Button from "./ui/Button";
import Chip from "./ui/Chip";
import RatingBadge from "./ui/RatingBadge";
import RatingTooltipBadge from "./ui/RatingTooltipBadge";
import PosterPlaceholder from "./ui/PosterPlaceholder";
import { cn } from "../utils/classNames";
import { formatDisplayDate } from "../utils/formatDisplayDate";
import { getDisplayPlatforms } from "../utils/networkNames";
import { normalizeSeasonNumber } from "../utils/showFields";
import { getPrimaryRating } from "../utils/showRatings";
import { shareShow } from "../utils/share";
import { BUTTON_LABELS } from "../constants/buttonLabels";
import { trackEvent } from "../utils/analytics";
import { getWatchlistToggleLabel } from "../utils/watchlist";
import useNow from "../hooks/useNow";

const ShowHero = ({
  show,
  isArchived = false,
  isInWatchlist = () => false,
  onToggleWatchlist = () => {},
  editorialSlot = null,
  className = "",
}) => {
  const {
    id,
    title,
    originalTitle,
    year,
    poster,
    releaseDate,
    seasonNumber,
    seasonPart,
    genres = [],
    contentRating,
    episodeCount,
    trailers = [],
    detailsUrl,
    alternateTitles = [],
  } = show;

  const primaryNetwork = getDisplayPlatforms(show);
  const trailerUrl = trailers.find((t) => t?.url)?.url || null;
  const inWatchlist = isInWatchlist?.(show);
  const titleVariants = [originalTitle, ...alternateTitles].filter(Boolean).slice(0, 3);

  const nowMs = useNow();
  const isUpcoming = Boolean(releaseDate) && new Date(releaseDate).getTime() > nowMs;
  const statusText = isArchived
    ? "Archived"
    : releaseDate
      ? isUpcoming
        ? "Upcoming"
        : "Now streaming"
      : "Release TBD";
  const releaseText = releaseDate
    ? `${isUpcoming ? "Premieres" : "Premiered"} ${formatDisplayDate(releaseDate)}`
    : "Date to be confirmed";
  const season = normalizeSeasonNumber(seasonNumber);
  // The year is the series' first; beside a later season it reads as that season's.
  const showYear = Boolean(year) && !(season > 1);
  // One plain line of data: "IMDb 8.4 · Rated 14 · 6 episodes · Season 6".
  const dataItems = [
    getPrimaryRating(show) !== null ? (
      <RatingTooltipBadge key="rating" show={show} isArchived={isArchived} plain tooltipClassName="max-w-sm" />
    ) : null,
    contentRating ? <span key="content-rating">Rated {contentRating}</span> : null,
    episodeCount ? <span key="episodes">{episodeCount} {episodeCount === 1 ? "episode" : "episodes"}</span> : null,
    season !== null ? <span key="season">Season {season}{seasonPart ? `, ${seasonPart}` : ""}</span> : null,
  ].filter(Boolean);

  const [failedPosterSrc, setFailedPosterSrc] = useState(null);
  const [heartPulse, setHeartPulse] = useState(false);
  const posterSrc = poster || null;
  const posterFailed = posterSrc && failedPosterSrc === posterSrc;

  const handleToggleWatchlist = useCallback(() => {
    onToggleWatchlist?.(show);
    setHeartPulse(true);
    setTimeout(() => setHeartPulse(false), 300);
  }, [onToggleWatchlist, show]);

  return (
    <div className={cn("grid gap-6 md:gap-8 lg:grid-cols-[320px_1fr]", className)}>
      {/* Poster */}
      <div className="relative mx-auto w-full max-w-[140px] sm:max-w-[320px] lg:mx-0">
        <div className="aspect-[2/3] overflow-hidden rounded-xl border border-accent-gold/70 shadow-card-base">
          {posterSrc && !posterFailed ? (
            <img
              src={posterSrc}
              alt={`${title} poster`}
              className="w-full h-full object-cover grayscale-[20%] contrast-110"
              fetchPriority="high"
              loading="eager"
              onError={() => setFailedPosterSrc(posterSrc)}
            />
          ) : (
            <PosterPlaceholder
              showId={id}
              className="w-full h-full bg-poster-placeholder-gradient"
            />
          )}
        </div>

        {/* Floating action buttons on poster */}
        <div className="mt-3 flex justify-center gap-2 sm:absolute sm:top-3 sm:left-3 sm:mt-0">
          <Button
            type="button"
            size="icon"
            variant="icon-dark"
            onClick={handleToggleWatchlist}
            className={cn(
              "h-11 w-11",
              inWatchlist && "border-primary bg-primary text-primary-foreground hover:bg-primary"
            )}
            aria-label={getWatchlistToggleLabel(title)}
            aria-pressed={Boolean(inWatchlist)}
          >
            <Heart className={cn("h-4 w-4", inWatchlist && "fill-current text-primary-foreground", heartPulse && "watchlist-pulse")} aria-hidden="true" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="icon-dark"
            onClick={() => shareShow(show)}
            className="h-11 w-11"
            aria-label={`Share ${title}`}
          >
            <Share2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>

        {/* NEW badge */}
        {seasonNumber === 1 && !seasonPart && !isArchived && (
          <RatingBadge variant="new" className="absolute top-3 right-3">
            NEW
          </RatingBadge>
        )}

      </div>

      {/* Content */}
      <div className="flex flex-col gap-4">
        {/* Title */}
        <div className="border-b border-dashed border-accent-gold/60 pb-4">
          <Typography variant="h1" className="text-foreground leading-tight">
            {title}
            {showYear && <span className="text-muted-foreground ml-2">({year})</span>}
          </Typography>
          {titleVariants.length > 0 && (
            <Typography variant="caption" className="text-muted-foreground mt-2 block">
              Also known as {titleVariants.join(" • ")}
            </Typography>
          )}
          <Typography variant="body" className="text-muted-foreground mt-1">
            {statusText}
            {primaryNetwork && ` · ${primaryNetwork}`}
          </Typography>
        </div>

        {/* Data line */}
        {dataItems.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-small text-meta text-foreground">
            {dataItems.map((item, index) => (
              <span key={item.key} className="whitespace-nowrap">
                {item}
                {index < dataItems.length - 1 && <span className="ml-2 text-muted-foreground" aria-hidden="true">·</span>}
              </span>
            ))}
          </div>
        )}

        {/* Genres */}
        {genres.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {genres.slice(0, 4).map((genre) => (
              <Chip key={genre} variant="outline">
                {genre}
              </Chip>
            ))}
          </div>
        )}

        {/* Release info */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground">
          <Typography variant="caption" className="text-label">
            {releaseText}
          </Typography>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3 mt-2">
          {trailerUrl && (
            <Button
              variant="default"
              size="default"
              onClick={() => {
                trackEvent("outbound_click", { surface: "show_hero", type: "trailer", slug: show.slug });
                window.open(trailerUrl, "_blank", "noopener,noreferrer");
              }}
            >
              <span className="flex items-center gap-2">
                <Play className="h-4 w-4" />
                {BUTTON_LABELS.WATCH_TRAILER}
              </span>
            </Button>
          )}
          {detailsUrl && (
            <Button
              variant="wireframe"
              size="default"
              onClick={() => {
                trackEvent("outbound_click", { surface: "show_hero", type: "details", slug: show.slug });
                window.open(detailsUrl, "_blank", "noopener,noreferrer");
              }}
            >
              <span className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                {BUTTON_LABELS.QUERY_IMDB}
              </span>
            </Button>
          )}
        </div>

        {editorialSlot}
      </div>
    </div>
  );
};

ShowHero.propTypes = {
  show: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    originalTitle: PropTypes.string,
    year: PropTypes.number,
    rating: PropTypes.number,
    poster: PropTypes.string,
    releaseDate: PropTypes.string,
    seasonNumber: PropTypes.number,
    seasonPart: PropTypes.string,
    genres: PropTypes.arrayOf(PropTypes.string),
    networks: PropTypes.arrayOf(PropTypes.string),
    contentRating: PropTypes.string,
    episodeCount: PropTypes.number,
    trailers: PropTypes.arrayOf(PropTypes.shape({ url: PropTypes.string })),
    detailsUrl: PropTypes.string,
    sourceName: PropTypes.string,
    alternateTitles: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isArchived: PropTypes.bool,
  isInWatchlist: PropTypes.func,
  onToggleWatchlist: PropTypes.func,
  editorialSlot: PropTypes.node,
  className: PropTypes.string,
};

export default ShowHero;
